import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User as UserIcon, Lock, Receipt, Trash2, Loader2, Check, AlertCircle, Gift, ShoppingBag,
} from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import BookCover from "../components/BookCover";
import { useToast } from "../hooks/use-toast";

const detailOf = (e) =>
  e?.response?.data?.detail ||
  (e?.response ? "Something went wrong." : "We could not reach Trellure. Check your connection.");

const inputCls =
  "w-full h-11 px-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all";

function Section({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card/50 p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-brand-500/15 border border-brand-500/30 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-brand-400" />
        </div>
        <div>
          <h2 className="font-semibold">{title}</h2>
          {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

const SOURCE_LABEL = {
  purchase: { icon: ShoppingBag, text: "Bought here" },
  redemption: { icon: Gift, text: "Redeemed with a code" },
  grant: { icon: Check, text: "Added by Trellure" },
};

export default function Account() {
  const { user, loading, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState("");
  const [closing, setClosing] = useState({ open: false, password: "" });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?next=/account", { replace: true });
      return;
    }
    setProfile({ name: user.name, email: user.email });
    api.get("/me/orders").then((r) => setOrders(r.data || [])).catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="py-32 flex justify-center" role="status" aria-label="Loading">
        <Loader2 className="w-6 h-6 text-muted-foreground/60 animate-spin" />
      </div>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy("profile");
    try {
      await api.put("/me/account", { name: profile.name, email: profile.email });
      await refreshMe();
      toast({ title: "Saved" });
    } catch (err) {
      toast({ title: "Could not save", description: detailOf(err), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy("password");
    try {
      await api.put("/me/account", pw);
      setPw({ currentPassword: "", newPassword: "" });
      toast({ title: "Password changed" });
    } catch (err) {
      toast({ title: "Could not change it", description: detailOf(err), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const closeAccount = async () => {
    setBusy("close");
    try {
      await api.delete("/me/account", { data: { password: closing.password } });
      logout();
      navigate("/", { replace: true });
      toast({ title: "Your account is closed." });
    } catch (err) {
      toast({ title: "Could not close it", description: detailOf(err), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-float-up min-h-[70vh]">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
          <UserIcon className="w-5 h-5 text-brand-400" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Your account</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="space-y-5">
        <Section icon={UserIcon} title="Your details">
          <form onSubmit={saveProfile} className="space-y-3">
            <div>
              <label htmlFor="ac-name" className="text-xs font-medium text-muted-foreground">Name</label>
              <input id="ac-name" className={inputCls + " mt-1.5"} value={profile.name} autoComplete="name"
                onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="ac-email" className="text-xs font-medium text-muted-foreground">Email</label>
              <input id="ac-email" type="email" className={inputCls + " mt-1.5"} value={profile.email} autoComplete="email"
                onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1.5">
                This is where receipts and reset links go, and it is how you sign in.
              </p>
            </div>
            <Button type="submit" disabled={busy === "profile"}
              className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full px-6">
              {busy === "profile" ? "Saving…" : "Save changes"}
            </Button>
          </form>
        </Section>

        <Section icon={Lock} title="Password">
          <form onSubmit={savePassword} className="space-y-3">
            <div>
              <label htmlFor="ac-cur" className="text-xs font-medium text-muted-foreground">Current password</label>
              <input id="ac-cur" type="password" autoComplete="current-password" className={inputCls + " mt-1.5"}
                value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
            </div>
            <div>
              <label htmlFor="ac-new" className="text-xs font-medium text-muted-foreground">New password</label>
              <input id="ac-new" type="password" autoComplete="new-password" className={inputCls + " mt-1.5"}
                value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1.5">At least 8 characters.</p>
            </div>
            <Button type="submit" disabled={busy === "password" || !pw.currentPassword || !pw.newPassword}
              className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full px-6">
              {busy === "password" ? "Changing…" : "Change password"}
            </Button>
          </form>
        </Section>

        <Section icon={Receipt} title="Your audiobooks"
          description={orders.length ? `${orders.length} in your library` : "Nothing yet"}>
          {orders.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              When you buy or redeem an audiobook it appears here, with the date and what you paid.{" "}
              <Link to="/browse" className="text-brand-400 hover:text-brand-300">Browse the catalogue</Link>.
            </p>
          ) : (
            <ul className="divide-y divide-border/60 -my-2">
              {orders.map((o) => {
                const meta = SOURCE_LABEL[o.source] || SOURCE_LABEL.grant;
                const Icon = meta.icon;
                return (
                  <li key={o.bookId} className="flex items-center gap-4 py-3">
                    <Link to={`/book/${o.slug}`} className="shrink-0">
                      <BookCover book={o} className="w-10 h-14 rounded-md cover-shadow" />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        <Link to={`/book/${o.slug}`} className="hover:text-brand-400 transition-colors">
                          {o.title_en}
                        </Link>
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Icon className="w-3 h-3" /> {meta.text}
                        {o.reference && <span className="font-mono">· {o.reference}</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium tabular-nums">
                        {o.pricePaid > 0 ? `£${Number(o.pricePaid).toFixed(2)}` : "Free"}
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {String(o.acquiredAt || "").slice(0, 10)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section icon={Trash2} title="Close your account"
          description="Removes your account and everything attached to it. This cannot be undone.">
          {!closing.open ? (
            <Button variant="outline" onClick={() => setClosing({ open: true, password: "" })}
              className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300">
              Close my account
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-3">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-200">
                  Your library, listening progress, wishlist and reviews are deleted with the account.
                  {orders.length > 0 && ` That includes ${orders.length} audiobook${orders.length === 1 ? "" : "s"}.`}
                </p>
              </div>
              <div>
                <label htmlFor="ac-del" className="text-xs font-medium text-muted-foreground">
                  Enter your password to confirm
                </label>
                <input id="ac-del" type="password" autoComplete="current-password" className={inputCls + " mt-1.5"}
                  value={closing.password} onChange={(e) => setClosing({ ...closing, password: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button onClick={closeAccount} disabled={busy === "close" || !closing.password}
                  className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold">
                  {busy === "close" ? "Closing…" : "Close my account permanently"}
                </Button>
                <Button variant="ghost" onClick={() => setClosing({ open: false, password: "" })}>Cancel</Button>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
